import React, { useState } from "react";
import styled from "@emotion/styled";
import DOC from "../../../vendor/DOC.png";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  Typography,
  TextField as MuiTextField,
} from "@mui/material";
import useAppSelector from "../../../hooks/useAppSelector";
import { RootState } from "../../../redux/store";
import useAppDispatch from "../../../hooks/useAppDispatch";
import { setLandingDialogState } from "../../../redux/slices/main";
import { addUserFeedback } from "../../../firebase/utils";
import { SnackbarFns } from "../../../utils/fnsRedux";

const MainBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled(Typography)`
  font-weight: bold;
  font-size: 1rem;
`;

const Section = styled.div`
  margin-top: 5px;
`;

const Body = styled(DialogContent)`
  padding-bottom: 0px;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const DocImage = styled.div`
  background-image: url(${DOC});
  background-size: contain;
  background-repeat: no-repeat;
  width: 100%;
  height: 75px;
`;

const YouTubeEmbed = styled.iframe`
  width: 560px;
  height: 315px;
  border: none;
  display: block;
  margin-top: 10px;
  margin-bottom: 10px;
`;

const TextField = styled(MuiTextField)`
  margin-top: 10px;
`;

interface ListItemTextProps {
  title: string;
  description: string;
}

const ListItemText: React.FC<ListItemTextProps> = ({ title, description }) => {
  return (
    <ListItem
      sx={{
        paddingTop: "0px",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
      }}
    >
      <Typography sx={{ fontWeight: "bold" }}>{title}</Typography>
      <Typography>{description}</Typography>
    </ListItem>
  );
};

function LandingDialog() {
  const state = useAppSelector((state: RootState) => state.main);
  const dispatch = useAppDispatch();
  const [feedback, setFeedback] = useState<string>("");
  const handleSnackbar = SnackbarFns();

  async function handleKeydown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      await addUserFeedback(feedback);
      handleSnackbar("success", "Thank you for your feedback!", true);
      setFeedback("");
    }
  }

  const terms = [
    {
      title: "Acceptance of Terms",
      description:
        "By accessing and using DocuSurf, you agree to be bound by these terms and conditions.",
    },
    {
      title: "User Obligations",
      description:
        "Users are responsible for ensuring the accuracy and legality of any content uploaded to DocuSurf.",
    },
    {
      title: "User Conduct",
      description:
        "Users must refrain from engaging in any unauthorized or unlawful activities while using DocuSurf.",
    },
    {
      title: "Privacy",
      description:
        "Our privacy policy outlines how we collect, store, and use user data. By using DocuSurf, you consent to the practices described in the privacy policy.",
    },
    {
      title: "Limitation of Liability",
      description:
        "DocuSurf is provided 'as is,' and we disclaim any warranties or liabilities associated with its use. Users assume all risks.",
    },
    {
      title: "Termination",
      description:
        "We reserve the right to terminate user access to DocuSurf in case of violation of these terms and conditions or any applicable laws.",
    },
    {
      title: "Modifications",
      description:
        "We may update or modify these terms and conditions from time to time. Users will be notified of any changes.",
    },
  ];

  return (
    <Dialog open={state.landingDialogOpen} onClose={() => undefined}>
      <DialogTitle>Welcome to DocuSurf!</DialogTitle>
      <Body sx={{ paddingBottom: "0px" }}>
        <MainBody>
          <Section>
            <Header>Introduction</Header>
            <Typography>
              Seamlessly uncover insights and accelerate learning with DocuSurf.
              We empower you to sift through your documents with unparalleled
              organization and efficiency.
            </Typography>
          </Section>
          <Section>
            <Header>How it Works</Header>
            <DocImage />
          </Section>
          <Section>
            <Header>Demo Video</Header>
            <YouTubeEmbed
              src={
                "https://www.youtube.com/embed/p4qjbqReTwY?si=gPZDbzb7CVWzOAa3"
              }
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="Embedded youtube"
            />
          </Section>
          <Section>
            <Header>Note</Header>
            <Typography>
              Please note that the maximum number of files is 10, and the total
              file storage limit is 50MB. Once either condition is reached, you
              will no longer be able to upload additional files. Currently, only
              pdf file types are supported.
            </Typography>
          </Section>
          <Section>
            <Header>Terms and Conditions</Header>
            <List>
              {terms.map((feature) => (
                <ListItemText
                  key={feature.title}
                  title={feature.title}
                  description={feature.description}
                />
              ))}
            </List>
          </Section>
          <Section>
            <Header>Further</Header>
            <Typography>
              Any further queries/issues/feedback can be directed through email
              to docu.ai.dev@gmail.com or telegram to @tinenhao
            </Typography>
          </Section>
          <Section>
            <Header>Feedback</Header>
            <Typography>
              We greatly value your input during this beta testing stage of
              DocuSurf. Your feedback is highly beneficial to our team as we
              strive to optimize and improve future results. Please let us know
              in the box below for any comments/feedback
            </Typography>
            <TextField
              margin="dense"
              label="Feedback (if any)"
              type="text"
              fullWidth
              multiline
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              onKeyDownCapture={(e) => handleKeydown(e)}
            />
          </Section>
        </MainBody>
      </Body>
      <DialogActions>
        <Button
          onClick={() => dispatch(setLandingDialogState(false))}
          color="primary"
        >
          I understand and wish to proceed
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default LandingDialog;
